import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, MapPin, Award, Sparkles, GraduationCap, Quote, Tv, Film, Disc, Plane, Instagram, Phone, Mic2 } from "lucide-react";
import { FOUNDERS } from "@/lib/data";

export default function FounderProfile() {
  const { id } = useParams();
  const founder = FOUNDERS.find((f) => f.id === id);
  if (!founder) return <Navigate to="/instructors" replace />;

  return (
    <div data-testid="founder-profile-page" className="bg-[#0a0a0a]">
      {/* Hero */}
      <section className="relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[60vh]">
          <div className="lg:col-span-5 relative overflow-hidden bg-[#0a0a0a]">
            <img
              src={founder.image}
              alt={founder.name}
              className="w-full h-full object-cover object-top aspect-[4/5] lg:aspect-auto"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </div>
          <div className="lg:col-span-7 flex flex-col justify-center p-8 lg:p-16 ember-bg relative">
            <Link
              to="/instructors"
              data-testid="back-to-mentors"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white/60 hover:text-[#ff4500] mb-6 self-start"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Founders & Our Team
            </Link>
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-3">◆ {founder.title}</p>
            <h1 className="font-display text-5xl lg:text-7xl text-white leading-[0.95]">
              {founder.name}
            </h1>
            <p className="mt-4 text-sm uppercase tracking-[0.2em] text-white/60 inline-flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#ff4500]" /> {founder.flag} {founder.city}, {founder.branch}
            </p>
            {founder.instagram && (
              <a
                href={`https://instagram.com/${founder.instagram}`}
                target="_blank"
                rel="noreferrer"
                data-testid={`founder-ig-${founder.id}`}
                className="mt-4 inline-flex items-center gap-2 px-4 h-10 border border-[#ffb700]/40 text-[#ffb700] hover:bg-[#ffb700] hover:text-black hover:border-[#ffb700] transition-colors text-sm font-semibold w-fit"
              >
                <Instagram className="w-4 h-4" /> @{founder.instagram}
              </a>
            )}
            {(founder.phone || founder.phone2) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {founder.phone && (
                  <a
                    href={`tel:${founder.phone.replace(/\s/g, "")}`}
                    data-testid={`founder-phone-${founder.id}`}
                    className="inline-flex items-center gap-2 px-4 h-10 border border-[#ff4500]/40 text-[#ff4500] hover:bg-[#ff4500] hover:text-white hover:border-[#ff4500] transition-colors text-sm font-semibold w-fit"
                  >
                    <Phone className="w-4 h-4" /> {founder.phone}
                  </a>
                )}
                {founder.phone2 && (
                  <a
                    href={`tel:${founder.phone2.replace(/\s/g, "")}`}
                    data-testid={`founder-phone2-${founder.id}`}
                    className="inline-flex items-center gap-2 px-4 h-10 border border-[#ff4500]/40 text-[#ff4500] hover:bg-[#ff4500] hover:text-white hover:border-[#ff4500] transition-colors text-sm font-semibold w-fit"
                  >
                    <Phone className="w-4 h-4" /> {founder.phone2}
                  </a>
                )}
              </div>
            )}
            <div className="mt-8 max-w-2xl space-y-4">
              {founder.bio.split("\n\n").map((p, i) => (
                <p key={i} className="text-base lg:text-lg text-white/75 leading-relaxed">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-16 border-y border-white/10 bg-[#070707]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <Quote className="w-10 h-10 text-[#ff4500] mb-4" strokeWidth={1.5} />
          <p className="font-display text-3xl lg:text-5xl text-white leading-tight italic">
            "{founder.quote}"
          </p>
          <p className="mt-6 text-xs uppercase tracking-[0.25em] text-[#ffb700]">— {founder.name}</p>
        </div>
      </section>

      {/* Details grid */}
      <section className="py-20 lg:py-28 max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Stats column */}
          <div className="lg:col-span-4 space-y-6">
            <div className="border border-white/10 bg-[#0a0a0a] p-7">
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Experience</p>
              <p className="font-display text-5xl phoenix-text mt-2">{founder.experience}</p>
            </div>
            <div className="border border-white/10 bg-[#0a0a0a] p-7">
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Branch</p>
              <p className="font-display text-3xl text-white mt-2">{founder.flag} {founder.city}</p>
              <p className="text-sm text-white/55 mt-1">{founder.branch}</p>
            </div>
            <div className="border border-white/10 bg-[#0a0a0a] p-7">
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3 flex items-center gap-2">
                <Sparkles className="w-3 h-3" /> Specialties
              </p>
              <div className="flex flex-wrap gap-2">
                {founder.specialties.map((s) => (
                  <span key={s} className="text-xs uppercase tracking-wider px-2 py-1 border border-[#ffb700]/30 text-[#ffb700]">
                    {s}
                  </span>
                ))}
              </div>
            </div>
            {founder.professionalStrengths && founder.professionalStrengths.length > 0 && (
              <div className="border border-[#ff4500]/40 bg-[#0a0a0a] p-7">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#ff4500] mb-4 flex items-center gap-2">
                  <Award className="w-3 h-3" /> Professional Strengths
                </p>
                <ul className="space-y-3">
                  {founder.professionalStrengths.map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-white/80 leading-snug">
                      <span className="font-display text-[#ffb700] shrink-0 leading-none mt-0.5">◆</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Achievements + Education */}
          <div className="lg:col-span-8 space-y-10">
            <div>
              <h2 className="font-display text-3xl lg:text-4xl text-white inline-flex items-center gap-3">
                <Award className="w-6 h-6 text-[#ff4500]" strokeWidth={1.5} />
                Achievements
              </h2>
              <ul className="mt-6 space-y-4">
                {founder.achievements.map((a, i) => (
                  <li key={i} className="flex items-start gap-3 text-base text-white/75 leading-relaxed">
                    <span className="font-display text-2xl phoenix-text shrink-0 leading-none mt-0.5">0{i + 1}</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="pt-8 border-t border-white/10">
              <h2 className="font-display text-3xl lg:text-4xl text-white inline-flex items-center gap-3">
                <GraduationCap className="w-6 h-6 text-[#ff4500]" strokeWidth={1.5} />
                Training & Education
              </h2>
              <ul className="mt-6 space-y-3">
                {founder.education.map((e, i) => (
                  <li key={i} className="flex items-start gap-3 text-base text-white/75 leading-relaxed">
                    <span className="w-2 h-2 mt-2.5 bg-[#ffb700] shrink-0" />
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Live Events & Brand Choreography */}
        {founder.liveEvents && founder.liveEvents.length > 0 && (
          <div className="mt-20 pt-12 border-t border-white/10">
            <h2 className="font-display text-3xl lg:text-4xl text-white inline-flex items-center gap-3 mb-8">
              <Mic2 className="w-6 h-6 text-[#ff4500]" strokeWidth={1.5} />
              Live Events & Brand Choreography
            </h2>
            <p className="text-sm text-white/55 mb-6 max-w-2xl">
              Headline event choreography for brand launches, celebrity celebrations, and televised galas.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {founder.liveEvents.map((e, i) => (
                <div key={i} data-testid={`live-event-${i}`} className="bg-[#0a0a0a] border border-[#ffb700]/30 p-6 hover:border-[#ffb700] hover:bg-[#0f0f0f] transition-colors">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#ffb700] mb-2">{e.role}</p>
                  <h4 className="font-display text-xl text-white leading-tight">{e.title}</h4>
                  {e.location && <p className="text-sm text-white/55 mt-2 inline-flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-[#ff4500]" /> {e.location}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reality Shows */}
        {founder.realityShows && founder.realityShows.length > 0 && (
          <div className="mt-20 pt-12 border-t border-white/10">
            <h2 className="font-display text-3xl lg:text-4xl text-white inline-flex items-center gap-3 mb-8">
              <Tv className="w-6 h-6 text-[#ff4500]" strokeWidth={1.5} />
              Reality Show Choreography
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
              {founder.realityShows.map((s, i) => (
                <div key={i} data-testid={`reality-show-${i}`} className="bg-[#0a0a0a] p-6 hover:bg-[#0f0f0f] transition-colors">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#ffb700]">{s.channel} · {s.year}</p>
                  <h4 className="font-display text-xl text-white mt-2 leading-tight">{s.show}</h4>
                  <p className="text-sm text-white/55 mt-2">{s.role}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Movie Audio Launches */}
        {founder.movieLaunches && founder.movieLaunches.length > 0 && (
          <div className="mt-20 pt-12 border-t border-white/10">
            <h2 className="font-display text-3xl lg:text-4xl text-white inline-flex items-center gap-3 mb-8">
              <Film className="w-6 h-6 text-[#ff4500]" strokeWidth={1.5} />
              Movie Audio Launches
            </h2>
            <p className="text-sm text-white/55 mb-6 max-w-2xl">
              Featured choreographer / performer at the audio launches of these productions.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {founder.movieLaunches.map((m, i) => (
                <div key={i} data-testid={`movie-launch-${i}`} className="border border-[#ff4500]/30 bg-[#0a0a0a] p-4 hover:border-[#ff4500] hover:bg-[#0f0f0f] transition-colors">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#ffb700] mb-1">Audio Launch</p>
                  <p className="font-display text-base text-white leading-tight">{m}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Album Choreography */}
        {founder.albumChoreography && founder.albumChoreography.length > 0 && (
          <div className="mt-20 pt-12 border-t border-white/10">
            <h2 className="font-display text-3xl lg:text-4xl text-white inline-flex items-center gap-3 mb-8">
              <Disc className="w-6 h-6 text-[#ff4500]" strokeWidth={1.5} />
              Album Choreography
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {founder.albumChoreography.map((a, i) => (
                <div key={i} data-testid={`album-${i}`} className="bg-[#0a0a0a] border border-white/10 p-6 card-hover flex items-center gap-5">
                  <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#ff4500] to-[#ffb700] shrink-0">
                    <Disc className="w-6 h-6 text-black" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-display text-xl text-white">{a.title}</h4>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#ffb700] mt-1">{a.year}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Overseas Workshops */}
        {founder.overseasWorkshops && founder.overseasWorkshops.length > 0 && (
          <div className="mt-20 pt-12 border-t border-white/10">
            <h2 className="font-display text-3xl lg:text-4xl text-white inline-flex items-center gap-3 mb-8">
              <Plane className="w-6 h-6 text-[#ff4500]" strokeWidth={1.5} />
              Overseas Dance Workshops
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-white/5">
              {founder.overseasWorkshops.map((w, i) => (
                <div key={i} data-testid={`workshop-${i}`} className="bg-[#0a0a0a] p-6 hover:bg-[#0f0f0f] transition-colors">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#ffb700]">{w.date}</p>
                  <h4 className="font-display text-lg text-white mt-2 leading-tight">{w.country}</h4>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="py-20 ember-bg border-t border-white/10">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-3">◆ Train with Phoenix</p>
            <h3 className="font-display text-3xl lg:text-4xl text-white leading-tight">
              Want to learn at the {founder.city} studio?
            </h3>
          </div>
          <Link to="/book-trial">
            <button data-testid="profile-cta-btn" className="btn-phoenix bg-[#ff4500] text-white px-8 h-14 uppercase tracking-wider text-sm font-semibold whitespace-nowrap">
              Book Free Trial
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
